import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import { Box, Stack, Typography } from "@mui/material";
import React from "react";

type StatusType = "success" | "warning" | "error" | "info" | "default";

interface InternStatusBadgeProps {
  status: StatusType;
  label: string;
  size?: "small" | "medium";
  showIcon?: boolean;
  variant?: "filled" | "outlined" | "subtle";
}

const STATUS_CONFIG: Record<
  StatusType,
  {
    icon: React.ReactNode;
    color: string;
    bgColor: string;
    borderColor: string;
  }
> = {
  success: {
    icon: <CheckCircleIcon />,
    color: "var(--intern-status-success)",
    bgColor: "var(--intern-status-success-bg)",
    borderColor: "var(--intern-status-success)",
  },
  warning: {
    icon: <WarningIcon />,
    color: "var(--intern-status-warning)",
    bgColor: "var(--intern-status-warning-bg)",
    borderColor: "var(--intern-status-warning)",
  },
  error: {
    icon: <ErrorIcon />,
    color: "var(--intern-status-error)",
    bgColor: "var(--intern-status-error-bg)",
    borderColor: "var(--intern-status-error)",
  },
  info: {
    icon: <InfoIcon />,
    color: "var(--intern-status-info)",
    bgColor: "var(--intern-status-info-bg)",
    borderColor: "var(--intern-status-info)",
  },
  default: {
    icon: <InfoIcon />,
    color: "var(--intern-text-secondary)",
    bgColor: "var(--intern-bg-secondary)",
    borderColor: "var(--intern-border-default)",
  },
};

const InternStatusBadge = ({
  status,
  label,
  size = "small",
  showIcon = false,
  variant = "subtle",
}: InternStatusBadgeProps) => {
  const config = STATUS_CONFIG[status];
  const isSmall = size === "small";

  const getStyles = () => {
    switch (variant) {
      case "filled":
        return {
          bgcolor: config.color,
          color: "#ffffff",
          border: "none",
        };
      case "outlined":
        return {
          bgcolor: "transparent",
          color: config.color,
          border: `1px solid ${config.borderColor}`,
        };
      case "subtle":
      default:
        return {
          bgcolor: config.bgColor,
          color: config.color,
          border: "none",
        };
    }
  };

  const styles = getStyles();

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: isSmall ? 1 : 1.5,
        py: isSmall ? 0.25 : 0.5,
        borderRadius: 1,
        ...styles,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {showIcon && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              "& svg": {
                fontSize: isSmall ? 14 : 16,
              },
            }}
          >
            {config.icon}
          </Box>
        )}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            fontSize: isSmall ? "0.6875rem" : "0.75rem",
            lineHeight: 1.4,
          }}
        >
          {label}
        </Typography>
      </Stack>
    </Box>
  );
};

export default InternStatusBadge;
